import { EditorContent, useCurrentEditor } from '@tiptap/react';
import { useEffect, HTMLProps, memo } from 'react';
import cn from 'classnames';
import { twMerge } from 'tailwind-merge';
import { useEditorRendererContext } from './Editor';

export interface EditorProps extends Omit<HTMLProps<HTMLDivElement>, 'ref'> {
  disableCopy?: boolean;
  onDisabledCopy?: () => void;
  textDirection?: 'rtl' | 'ltr';
  content?: string;
}

function Preview({
  className,
  content,
  disableCopy,
  onDisabledCopy,
  textDirection,
  ...props
}: EditorProps) {
  const { editor } = useCurrentEditor();

  const { editorRef } = useEditorRendererContext();

  useEffect(
    function updateReadOnly() {
      editor?.setEditable(false);
    },
    [editor],
  );

  useEffect(
    function updateDisableCopyPaste() {
      if (!editor) return;

      editor.commands.updateDisableCopy(!!disableCopy);
    },
    [editor, disableCopy],
  );

  useEffect(
    function updateDisableCopyPasteEventHandlers() {
      if (!editor) return;
      editor.storage.noCopyPaste.onDisabledCopy = onDisabledCopy;
    },
    [editor, onDisabledCopy],
  );

  return (
    <EditorContent
      {...props}
      className={twMerge(
        cn(
          'relative w-full placeholder:text-muted-foreground px-2 py-2',
          { 'text-right': textDirection === 'rtl' },
          className,
        ),
      )}
      dir={textDirection}
      editor={editor}
      ref={editorRef}
    />
  );
}

export default memo(Preview);
